import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, makeStyles, TextField, Typography, FormControl, InputLabel, MenuItem, Select, FormHelperText } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import AppDataContext from '../../common/AppContext';
import UserProfileContext from '../../common/ProfileContext';
import Validations from '../../common/Validations';
import { authenticationService, userService } from '../../services';
import Helper from '../../common/Helper';
import { eventCalendarService, referenceDataService } from '../../services';
import AppConstants from "../../common/AppConstants";
import Loader from "../../sharedComponents/Loader/Loader";

const useStyles = makeStyles((theme) => ({
    dialogTitle: {
        background: theme.palette.warning.main,
        color: "#fff",
        padding: theme.spacing(1, 2)
    },
    form: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(1),

    },
    infoText: {
        background: '#c8e2f7',
        padding: '12px',
        borderRadius: '12px',
        '& span': {
            backgroundColor: '#c8e2f7 !important',
        }
    },
    rsvpToggleGroup: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: theme.spacing(2),
        [theme.breakpoints.down('xs')]: {
            gridTemplateColumns: '1fr'
        }
    },
    rsvpToggleButton: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1.5),
        padding: theme.spacing(1.5, 2),
        justifyContent: 'flex-start',
        textTransform: 'none',
        backgroundColor: theme.palette.background.paper,
        transition: 'all 0.2s ease',
        '&:hover': {
            borderColor: theme.palette.primary.light,
            backgroundColor: '#f7fbff'
        }
    },
    rsvpToggleButtonActive: {
        borderColor: theme.palette.primary.main,
        backgroundColor: '#eef6ff',
        boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.15) inset'
    },
    rsvpToggleContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        lineHeight: 1.2
    },
    rsvpToggleTitle: {
        fontWeight: 700
    },
    rsvpToggleSubtitle: {
        marginTop: theme.spacing(0.5),
        color: theme.palette.text.secondary
    },
    sizeOptionRow: {
        display: 'flex',
        gap: theme.spacing(1),
        flexWrap: 'wrap'
    },
    sizeOptionButton: {
        minWidth: 56,
        borderRadius: 999,
        border: `1px solid ${theme.palette.divider}`,
        textTransform: 'none',
        fontWeight: 600
    },
    sizeOptionButtonActive: {
        borderColor: theme.palette.primary.main,
        backgroundColor: '#eef6ff'
    }
}));

export default function EditProfileValidationDialog() {
    const classes = useStyles();
    const { user, setUser, addSnackMessages, countryList, getCountryList, clinicEvents, eventCategoryList, getCategoryList, getEvents } = useContext(AppDataContext);
    const { country } = useContext(UserProfileContext);
    const [open, setOpen] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({});
    const [formDataError, setFormDataError] = useState([]);
    const [incompletedFields, setIncompletedFields] = useState([]);
    const [currentIncompleteField, setCurrentIncompleteField] = useState();
    const [isLoadingEvents, setIsLoadingEvents] = useState(false);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [notes, setNotes] = useState('')
    const [pendingRsvpEvents, setPendingRsvpEvents] = useState([])
    const [rsvpFormData, setRsvpFormData] = useState({ eventID: 0, rsvpResponse: null, declineReason: '', jerseySize: null })

    const selectedPendingEvent = useMemo(() => {
        return pendingRsvpEvents.find(x => x.eventID === rsvpFormData.eventID) ?? null;
    }, [pendingRsvpEvents, rsvpFormData.eventID]);

    useEffect(() => {
        getAppConfig();
        if (countryList === undefined || (countryList && countryList.length === 0)) {
            getCountryList();
        }
        if (eventCategoryList.length === 0) {
            getCategoryList();
        }
        if (clinicEvents.length === 0) {
            getEvents();
        }
        if (user && user.incompletedFields && user.incompletedFields.length > 0) {
            setIncompletedFields(user.incompletedFields)
            let data = {}
            if (user.incompletedFields.includes('country')) {
                data['country'] = 0
            }
            if (user.incompletedFields.includes('aboutUsSource')) {
                data['aboutUsSourceID'] = null
                data['eventID'] = 0
                data['aboutUsDescription'] = ''
                data['previousAboutUsSourceID'] = 0
                data['previousEventID'] = 0
                data['isProfileCreatedForEvent'] = false
            }
            if (user.incompletedFields.includes('rsvpResponse')) {
                setIsLoadingEvents(true);
                userService.getOfficiatingEvents(user.userName).then(res => {
                    const officiatingEvents = res?.officiatingEvents ?? [];
                    const today = new Date(Helper.getTodayDate());
                    const pendingEvents = officiatingEvents.filter(e => (e.eventType === 'CampEvent' || e.eventType === 'VTSEvent')
                     && (e.confirmation === null || e.confirmation === 0)
                     && !!e.eventStart
                     && new Date(e.eventStart.substring(0, 10).replace(/-/g, '/')) >= today);
                    setPendingRsvpEvents(pendingEvents);
                    if (pendingEvents.length > 0) {
                        setRsvpFormData({ eventID: pendingEvents[0].eventID, rsvpResponse: null, declineReason: '', jerseySize: null });
                    }
                }).catch(err => {
                    console.log('Error fetching officiating events:', err);
                }).finally(() => setIsLoadingEvents(false));
            }
            setFormData({ ...data })
        }
    }, []);

    const getAppConfig = () => {
        referenceDataService.getAppConfig({ configKey: AppConstants.ABOUT_US_CONFIG_KEY })
            .then(res => {
                if (res) {
                    setNotes(res.configValue)
                }
            }).catch(err => {
                console.log(err);
            })
    }

    useEffect(() => {
        if (incompletedFields.length > 0) {
            let data = [...incompletedFields]
            let current = data.pop()
            setCurrentIncompleteField(current)
        } else {
            //  setUser({ ...user, isProfileComplete: true });
        }
    }, [incompletedFields]);


    useEffect(() => {
        isFormDataValid();
    }, [formData, rsvpFormData, currentIncompleteField]);

    useEffect(() => {
        if (
            currentIncompleteField === AppConstants.IncompletedFields.RsvpResponse &&
            !isLoadingEvents &&
            pendingRsvpEvents.length === 0
        ) {
            updateIncompletedFields();
        }
    }, [currentIncompleteField, isLoadingEvents, pendingRsvpEvents]);



    const updateIncompletedFields = () => {
        let data = [...incompletedFields]
        data.pop()
        if (data.length === 0) {
            //Helper.updateLocalStorageData('currentUser', { isProfileComplete: true });
            authenticationService.setCurrentUser({ ...user, isProfileComplete: true, incompletedFields: [] });
            setUser({ ...user, isProfileComplete: true, incompletedFields: [] });
        }
        //else {
        setIncompletedFields(data ?? [])
        //}

    }

    const generateEventMenu = (option) => {
        if (Object.keys(option).length === 0) {
            return '';
        } else {
            const eventDate = option.eventStart === option.eventEnd
                ? Helper.FormatMMDDYY(option.eventStart)
                : `${Helper.FormatMMDDYY(option.eventStart)} to ${Helper.FormatMMDDYY(option.eventEnd)}`;

            return `${option.eventName ? option.eventName : ""}${option.eventMode && option.eventMode === "Virtual" ? "" : " at "}${option.eventCity ? option.eventCity + ", " : ""}${option.eventCountry === 1 && option.eventStateName ? option.eventStateName + ", " : ""}${option.eventCountry > 1 && option.eventStateOther ? option.eventStateOther + ", " : ""}${option.eventCountryName ? option.eventCountryName : ""}${option !== null ? ` (${eventDate})` : ''}`;

        }
    };



    const isFormDataValid = () => {
        let errors = [];
        Object.entries(formData).forEach(([key, value]) => {
            let field = false;
            switch (key) {
                case "country":
                    if (currentIncompleteField === AppConstants.IncompletedFields.Country) {
                        field = Validations.Validate("Country", value, { Min: 1 });
                        if (field && !field.isValid) {
                            field.validationMessage = "Country is required."
                        }
                    }
                    break;
                case "aboutUsSourceID":
                    field = Validations.Validate("Field", value, { Required: true });
                    break;
                case "eventID":
                    if (selectedCategory && selectedCategory.aboutUsDescriptionInputType === 'eventDropdown' && selectedCategory.isAboutUsDescriptionMandatory)
                        field = Validations.Validate("Event", value, { Min: 1 });
                    if (field && !field.isValid) {
                        field.validationMessage = "Event is required."
                    }
                    break;
                case "aboutUsDescription":
                    if (selectedCategory && selectedCategory.aboutUsDescriptionInputType === 'freeTextbox' && selectedCategory.isAboutUsDescriptionMandatory) {
                        field = Validations.Validate("Detail", value, { Required: true });
                    }
                    break;
            }
            if (field && !field.isValid) {
                errors[key] = field.validationMessage[0].toUpperCase() + field.validationMessage.slice(1);
            }
        });

        // Validate RSVP response
        if (currentIncompleteField === AppConstants.IncompletedFields.RsvpResponse) {
            if (rsvpFormData.eventID === 0) {
                errors['eventID'] = 'Please select an event';
            }
            if (rsvpFormData.rsvpResponse === null) {
                errors['rsvpResponse'] = 'Please select Yes or No';
            }
            if (
                rsvpFormData.rsvpResponse === 1 &&
                selectedPendingEvent &&
                selectedPendingEvent.eventType === 'CampEvent' &&
                !rsvpFormData.jerseySize
            ) {
                errors['jerseySize'] = 'Please select jersey size';
            }
            if (rsvpFormData.rsvpResponse === 2 && (!rsvpFormData.declineReason || rsvpFormData.declineReason.trim() === '')) {
                errors['declineReason'] = 'Please provide a reason for declining';
            }
        }

        if (Object.keys(errors).length) {
            setFormDataError(errors);
            return false;
        }
        setFormDataError([]);
        return true;
    }

    const handleFormSubmit = (e) => {
        e.preventDefault();
        console.log(formData)
        setIsLoading(true);
        if (!isFormDataValid()) {
            setIsLoading(false);
            return false;
        }

        //api service for updating country         
        if (currentIncompleteField === AppConstants.IncompletedFields.Country) {
            userService.saveMandatoryContactInfo(formData).then(res => {
                //  Helper.updateLocalStorageData('currentUser', { isProfileComplete: true });
                updateIncompletedFields()
                addSnackMessages([{
                    open: true,
                    message: res.message,
                    severity: 'success'
                }], true);
                setIsLoading(false);
            }).catch(e => {
                addSnackMessages([{
                    open: true,
                    message: e.message,
                    severity: 'error'
                }], true);
                setIsLoading(false);
            })
        } else if (currentIncompleteField === AppConstants.IncompletedFields.AboutUsSource) {
            userService.saveAboutUsInfo(formData).then(res => {
                updateIncompletedFields()
                addSnackMessages([{
                    open: true,
                    message: res.message,
                    severity: 'success'
                }], true);
                setIsLoading(false);
            }).catch(e => {
                addSnackMessages([{
                    open: true,
                    message: e.message,
                    severity: 'error'
                }], true);
                setIsLoading(false);
            })

        } else if (currentIncompleteField === AppConstants.IncompletedFields.RsvpResponse) {
            const rsvpPayload = {
                eventID: rsvpFormData.eventID,
                officialuid: user.officialUID,
                invitationResponse: rsvpFormData.rsvpResponse,
                jerseySize: rsvpFormData.rsvpResponse === 1 && selectedPendingEvent && selectedPendingEvent.eventType === 'CampEvent' ? rsvpFormData.jerseySize : null,
                declineReason: rsvpFormData.rsvpResponse === 2 ? rsvpFormData.declineReason.trim() : null
            };
            eventCalendarService.saveCandidateCampInvitationResponse(rsvpPayload).then(res => {
                const remainingPendingEvents = pendingRsvpEvents.filter(x => x.eventID !== rsvpFormData.eventID);
                setPendingRsvpEvents(remainingPendingEvents);

                if (remainingPendingEvents.length > 0) {
                    setRsvpFormData({ eventID: remainingPendingEvents[0].eventID, rsvpResponse: null, declineReason: '', jerseySize: null });
                } else {
                    updateIncompletedFields();
                    setRsvpFormData({ eventID: 0, rsvpResponse: null, declineReason: '', jerseySize: null });
                }

                addSnackMessages([{
                    open: true,
                    message: res.message || 'RSVP saved successfully',
                    severity: 'success'
                }], true);
                setIsLoading(false);
            }).catch(e => {
                addSnackMessages([{
                    open: true,
                    message: e.message,
                    severity: 'error'
                }], true);
                setIsLoading(false);
            })
        }

    }

    const getLocationDisplay = useMemo(() => {
        if(selectedPendingEvent === null) {
            return "";
        }
        if (selectedPendingEvent?.eventMode === "Virtual") {
            return "";
        }
        const state = selectedPendingEvent.eventState !== 0 ? selectedPendingEvent.eventStateName : selectedPendingEvent.eventStateOther;
        return [selectedPendingEvent.eventCity, state, selectedPendingEvent.countryName].filter(Boolean).join(', ');
    }, [selectedPendingEvent]);

    return <Dialog PaperProps={{
        component: "form",
        noValidate: true,
        onSubmit: (e) => handleFormSubmit(e)
    }} open={open} disableEscapeKeyDown maxWidth='md' fullWidth={true}>
        <DialogTitle disableTypography className={classes.dialogTitle}>
            <Typography variant='h4'>Attention Required!</Typography>
        </DialogTitle>
        <DialogContent dividers style={{ minHeight: '220px' }}>
            {currentIncompleteField === AppConstants.IncompletedFields.RsvpResponse ? (
                <>
                    <Typography gutterBottom>
                        Welcome back, you have pending event invitations that require your RSVP.
                    </Typography>
                    <Typography gutterBottom>
                        Please complete your RSVP to continue.
                    </Typography>
                </>
            ) : (
                <>
                    <Typography gutterBottom>
                        Welcome back, we found that following fields are missing in your profile.
                    </Typography>
                    <Typography gutterBottom>
                        Please update these to continue.
                    </Typography>
                </>
            )}


            {currentIncompleteField === AppConstants.IncompletedFields.Country &&
                <Grid container spacing={2} className={classes.form}>
                    <Grid item xs={12}>
                        <Autocomplete
                            id={`country`}
                            size="small"
                            disableClearable
                            value={countryList.find(x => x.numericValue === formData.eventCountry)}
                            options={countryList}
                            getOptionLabel={(option) => option.displayName ? option.displayName : ""}
                            onChange={(event, newValue) => {
                                let val = newValue !== null ? newValue.numericValue : 0;
                                setFormData({ ...formData, country: val });
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    fullWidth
                                    variant="outlined"
                                    label={`Country`}
                                    placeholder="Please select country"
                                    error={formDataError.country && true || false}
                                    helperText={formDataError.country}
                                    required
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            }
            {currentIncompleteField === AppConstants.IncompletedFields.AboutUsSource &&
                <Grid container spacing={2} className={classes.form}>
                    <Grid item xs={12}>
                        <FormControl variant="outlined"
                            className={classes.formControl}
                            fullWidth
                            size="small"
                            required
                            error={formDataError.aboutUsSourceID && true || false}
                        >
                            <InputLabel id="demo-simple-select-outlined-label">Please select how you heard about us below.</InputLabel>
                            <Select id="aboutUsSource"
                                name="aboutUsSource"
                                required
                                value={formData.aboutUsSourceID}
                                onChange={(e) => {
                                    setFormData({ ...formData, aboutUsSourceID: e.target.value, eventID: 0, aboutUsDescription: '', isProfileCreatedForEvent: false })
                                    setFormDataError([])
                                    setFilteredEvents(clinicEvents.filter(x => x.eventCategoryID === e.target.value))
                                    let cat = eventCategoryList.find(x => x.id === e.target.value)
                                    setSelectedCategory(cat)
                                    setFormDataError([])
                                }}
                                fullWidth
                                label="Please select how you heard about us below."
                            >
                                <MenuItem value=""></MenuItem >
                                {eventCategoryList.filter(item => item.isActive && item.showOnAboutUs).map((option, index) => (
                                    <MenuItem disabled={clinicEvents.filter(x => x.eventCategoryID === option.id).length === 0 && option.aboutUsDescriptionInputType === 'eventDropdown'} key={index} value={option.id}>
                                        {option.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formDataError.aboutUsSourceID && <FormHelperText>{formDataError.aboutUsSourceID}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        {selectedCategory && selectedCategory.aboutUsDescriptionInputType && selectedCategory.aboutUsDescriptionInputType === 'eventDropdown' &&
                            <Autocomplete
                                id={`event`}
                                size="small"
                                disableClearable
                                value={clinicEvents.find(x => x.eventID === formData.eventID) ?? {}}
                                options={filteredEvents}
                                getOptionLabel={(option) => generateEventMenu(option)}
                                onChange={(event, newValue) => {
                                    let val = newValue !== null ? newValue.eventID : 0;
                                    let newFormValues = { ...formData, eventID: val }
                                    if (newValue !== null && newValue.eventID) {
                                        newFormValues['isProfileCreatedForEvent'] = true
                                    }
                                    setFormData(newFormValues);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        variant="outlined"
                                        label={`Event`}
                                        placeholder="Please select Event"
                                        error={formDataError.eventID && true || false}
                                        helperText={formDataError.eventID}
                                    />
                                )}
                            />}
                        {selectedCategory && selectedCategory.aboutUsDescriptionInputType && selectedCategory.aboutUsDescriptionInputType === 'freeTextbox' &&
                            <TextField
                                variant="outlined"
                                id="aboutusDescription"
                                name="aboutusDescription"
                                size="small"
                                label="Details"
                                fullWidth
                                value={formData.aboutUsDescription}
                                onChange={(e) => setFormData({ ...formData, aboutUsDescription: e.target.value })}
                                error={formDataError.aboutUsDescription && true || false}
                                helperText={formDataError.aboutUsDescription}

                            ></TextField>
                        }
                    </Grid>


                </Grid>

            }
            {currentIncompleteField === AppConstants.IncompletedFields.RsvpResponse &&
                <Grid container spacing={2} className={classes.form}>
                    <Grid item xs={12}>
                        <FormControl variant="outlined" fullWidth size="small" required error={formDataError.eventID && true || false}>
                            <InputLabel>Please select an event</InputLabel>
                            <Select
                                value={rsvpFormData.eventID}
                                onChange={(e) => {
                                    setRsvpFormData({ ...rsvpFormData, eventID: e.target.value, rsvpResponse: null, declineReason: '', jerseySize: null });
                                    setFormDataError([]);
                                }}
                                label="Please select an event"
                            >
                                <MenuItem value={0}></MenuItem>
                                {pendingRsvpEvents.map((event, index) => (
                                    <MenuItem key={index} value={event.eventID}>
                                        {event.eventName}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formDataError.eventID && <FormHelperText>{formDataError.eventID}</FormHelperText>}
                        </FormControl>
                    </Grid>
                    {rsvpFormData.eventID > 0 && selectedPendingEvent && (
                        <Grid item xs={12}>
                            <Grid container spacing={1} mb={2} p={1} style={{ backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" gutterBottom>
                                        <strong>Event:</strong> {selectedPendingEvent.eventName}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2" gutterBottom>
                                        <strong>Date:</strong> {selectedPendingEvent.eventStart ? Helper.FormatMMDDYY(selectedPendingEvent.eventStart) : ''}
                                    </Typography>
                                </Grid>

                                {getLocationDisplay && getLocationDisplay != "" && <Grid item xs={6}>
                                    <Typography variant="body2">
                                        <strong>Location:</strong> {getLocationDisplay || '-'}
                                    </Typography>
                                </Grid>}
                            </Grid>

                            {/* <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>


                             
                            </div> */}
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Typography gutterBottom>
                            Will you attend?
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <div className={classes.rsvpToggleGroup}>
                            <Button
                                className={`${classes.rsvpToggleButton} ${rsvpFormData.rsvpResponse === 1 ? classes.rsvpToggleButtonActive : ''}`}
                                onClick={() => {
                                    setRsvpFormData({ ...rsvpFormData, rsvpResponse: 1, declineReason: '' });
                                    setFormDataError([]);
                                }}
                            >
                                <div className={classes.rsvpToggleContent}>
                                    <Typography variant="subtitle2" className={classes.rsvpToggleTitle}>Yes, I will attend</Typography>
                                    <Typography variant="caption" className={classes.rsvpToggleSubtitle}>Confirm my participation</Typography>
                                </div>
                            </Button>
                            <Button
                                className={`${classes.rsvpToggleButton} ${rsvpFormData.rsvpResponse === 2 ? classes.rsvpToggleButtonActive : ''}`}
                                onClick={() => {
                                    setRsvpFormData({ ...rsvpFormData, rsvpResponse: 2, jerseySize: null });
                                    setFormDataError([]);
                                }}
                            >
                                <div className={classes.rsvpToggleContent}>
                                    <Typography variant="subtitle2" className={classes.rsvpToggleTitle}>No, I can&apos;t attend</Typography>
                                    <Typography variant="caption" className={classes.rsvpToggleSubtitle}>Decline this invitation</Typography>
                                </div>
                            </Button>
                        </div>
                    </Grid>
                    {rsvpFormData.rsvpResponse === 2 && (
                        <Grid item xs={12}>
                            <TextField
                                multiline
                                rows={3}
                                fullWidth
                                variant="outlined"
                                label="Reason"
                                value={rsvpFormData.declineReason}
                                onChange={(e) => {
                                    setRsvpFormData({ ...rsvpFormData, declineReason: e.target.value });
                                    setFormDataError([]);
                                }}
                                error={formDataError.declineReason && true || false}
                                helperText={formDataError.declineReason}
                                placeholder="Enter your reason here..."
                                required
                            />
                        </Grid>
                    )}
                    {rsvpFormData.rsvpResponse === 1 && selectedPendingEvent && selectedPendingEvent.eventType === 'CampEvent' && (
                        <Grid item xs={12}>
                            <Typography gutterBottom>
                                Please select jersey size.
                            </Typography>
                            <div className={classes.sizeOptionRow}>
                                {['S', 'M', 'L', 'XL', 'XXL'].map((size) => (
                                    <Button
                                        key={size}
                                        className={`${classes.sizeOptionButton} ${rsvpFormData.jerseySize === size ? classes.sizeOptionButtonActive : ''}`}
                                        onClick={() => {
                                            setRsvpFormData({ ...rsvpFormData, jerseySize: size });
                                            setFormDataError([]);
                                        }}
                                    >
                                        {size}
                                    </Button>
                                ))}
                            </div>
                            {formDataError.jerseySize && (
                                <FormHelperText error>{formDataError.jerseySize}</FormHelperText>
                            )}
                        </Grid>
                    )}
                </Grid>
            }
            {/* {formData.aboutUsSource === 'Other' &&
                <Grid item xs={12}>
                    <TextField
                        variant="outlined"
                        id="aboutusDescription"
                        name="aboutusDescription"
                        size="small"
                        label="Details"
                        fullWidth
                        required
                        value={formData.aboutUsDescription}
                        onChange={(e) => setFormData({ ...formData, aboutUsDescription: e.target.value })}
                        error={formDataError.aboutUsDescription && true || false}
                        helperText={formDataError.aboutUsDescription}

                    ></TextField>
                </Grid>
            } */}
        </DialogContent>
        <DialogActions>
            <Button
                color="primary"
                variant="contained"
                type="submit"
                className="capitalize"
                disabled={(isLoading || Object.keys(formDataError).length > 0)}
                startIcon={isLoading && <CircularProgress color="secondary" size={24} />}
            >
                Save & Next
            </Button>
        </DialogActions>
        {currentIncompleteField === AppConstants.IncompletedFields.AboutUsSource && notes !== '' &&
            < div className={classes.infoText} dangerouslySetInnerHTML={{ __html: notes ?? '' }}>
            </div>
        }
        <Loader isLoading={isLoadingEvents} />
    </Dialog >;
}
